import log from 'loglevel';
import cloneDeep from 'lodash.clonedeep';

import { ConfigurationManager } from '../configuration-manager/configuration-manager';
import { getBrowserContext } from '../browser-utilities/browser-context';
import { PollingManager } from '../polling-manager/polling-manager';
import { RequestManager, DefaultResponseBody } from '../browser-utilities/request-manager';

import createTriggerTrigger from '../triggers/create-trigger.trigger';
import trackEventTrigger from '../triggers/track-event.trigger';
import identifyEventTrigger from '../triggers/identify-event.trigger';
import updateEventTrigger from '../triggers/update-event.trigger';
import getOptOutUrlTrigger from '../triggers/get-opt-out-url.trigger';

export interface DefaultEventRequestBody {
  context: any;
  timestamp: string;
  xngGlobalUserId: string;
  xngSessionId: string;
  type: 'track'; // Note: seems to be needed for BE to recognize as event, but in case of pageView event it will be 'page' and still work ¯\_(ツ)_/¯
  properties?: any // not every event seems to send this
}

/**
 * The TriggerManager registers triggers and executes them with the required arguments.
 * Most triggers will trigger 
 */
export class TriggerManager {
  private static registeredTriggers = new Map<string, (...args: any[]) => void | Promise<unknown>>([
    ['track', trackEventTrigger],
    ['identify', identifyEventTrigger],
    ['update', updateEventTrigger],
    ['createTrigger', createTriggerTrigger],
    ['getOptOutUrl', getOptOutUrlTrigger]
  ]);

  /**
   * Tries to register a new trigger. Logs an error if a trigger with the same name is already registered.
   * @param name The name of the trigger
   * @param fn The function to be executed
   */
  static registerTrigger(name: string, fn: (...args: any[]) => void | Promise<void>) {
    if (this.registeredTriggers.has(name)) {
      log.error(`Could not register new trigger '${name}': Already registered.`);
    } else {
      this.registeredTriggers.set(name, fn);
    }
  }

  /**
   * Executes a trigger with the name, with the provided parameters. Logs an error if that trigger does not exist.
   * @param name The name of the trigger
   * @param args An array of the arguments for the trigger function
   */
  static executeTrigger(name: string, args: any[]): Promise<unknown> {
    const triggerFn = this.registeredTriggers.get(name);
    if (!triggerFn) {
      log.error(`Can not execute '${name}': Not a registered trigger.`);
      return new Promise(resolve => resolve());
    } else {
      return triggerFn(...args) || Promise.resolve();
    };
  }

  /**
   * Makes a POST request to the API and aggregates the payload with the default required data for events. Also updates the configuration on response.
   * This method should be used for any API call that get made by an event trigger and reflects as an user event on the server.
   * @param endpoint the url part of the endpoint
   * @param payload the body of the POST request
   */
  static eventRequest<T>(endpoint: string, payload: T): Promise<DefaultResponseBody | void> {
    let requestBody = Object.assign(this.getDefaultEventPayloadData(), payload);
    requestBody.properties = this.addGlobalEventProperties(requestBody.properties);

    log.debug(`xhr:POST event request body: `, requestBody);

    return RequestManager.request<DefaultResponseBody>(endpoint, 'POST', requestBody)
      .then(data => data && this.processEventResponseData(data))
      .then(() => {
        // resets the polling interval, which will result in a very quick polling after the request.
        PollingManager.resetPollingInterval();
      });
  }

  /**
   * Assigns `globalEventProperties` that are set by configuration.
   * @param the original properties
   */
  static addGlobalEventProperties<T>(properties: T): T & any {
    const globalProps = ConfigurationManager.get('globalEventProperties');
    if (globalProps && Object.keys(globalProps).length > 0) {
      log.debug(`Applying globalEventProperties: ${globalProps}`);
      return Object.assign(cloneDeep(globalProps),  cloneDeep(properties || {}));
    } else {
      return properties;
    }
  }

  /**
   * Gathers the default payload data required for all calls to the tracking API.
   */
  private static getDefaultEventPayloadData(): DefaultEventRequestBody {
    return {
      xngGlobalUserId: ConfigurationManager.get('globalUserId'),
      xngSessionId: ConfigurationManager.get('sessionId'),
      timestamp: new Date().toISOString(),
      context: getBrowserContext(),
      type: 'track'
    };
  };

  /**
   * Applies side effects of the event response data before returning the response payload.
   * @param data the response data.
   */
  private static processEventResponseData(responseData: DefaultResponseBody): DefaultResponseBody {
    ConfigurationManager.applyConfiguration({
      globalUserId: responseData.xngGlobalUserId,
      sessionId: responseData.xngSessionId
    });
    return responseData;
  }
}
